import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Zap, Package, Tag, Calendar, TrendingDown,
  ChevronDown, ChevronUp, CheckCircle2, X,
  Sparkles, Undo2, Lock, SkipForward, Search,
  ShoppingCart, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { smartActionsAPI, stockAPI, suppliersAPI } from '@/lib/api';
import { WhatsAppPreviewModal, OrderCartPanel, fmtTimestamp } from '@/components/PurchaseOrderCart';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

// ── Constants ────────────────────────────────────────────────────────────────
const TYPE_META = {
  reorder:        { label: 'Low Stock',  icon: Package,      color: 'blue'   },
  dead_stock:     { label: 'Dead Stock', icon: Tag,          color: 'amber'  },
  expiry:         { label: 'Expiry',     icon: Calendar,     color: 'red'    },
  margin_leakage: { label: 'Margin',     icon: TrendingDown, color: 'orange' },
};

const PRIORITY_META = {
  critical: { label: 'Critical', dot: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50'    },
  high:     { label: 'High',     dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50' },
  medium:   { label: 'Medium',   dot: 'bg-blue-400',   text: 'text-blue-700',   bg: 'bg-blue-50'   },
};

const COLOR_CLASSES = {
  blue:   { badge: 'bg-blue-100 text-blue-700',     icon: 'text-blue-600'   },
  amber:  { badge: 'bg-amber-100 text-amber-700',   icon: 'text-amber-600'  },
  red:    { badge: 'bg-red-100 text-red-700',       icon: 'text-red-600'    },
  orange: { badge: 'bg-orange-100 text-orange-700', icon: 'text-orange-600' },
};

const FILTERS = [
  { id: 'all',            label: 'All' },
  { id: 'critical',       label: 'Critical' },
  { id: 'dead_stock',     label: 'Dead Stock' },
  { id: 'low_stock',      label: 'Low Stock' },
  { id: 'margin_leakage', label: 'Margin Leakage' },
  { id: 'expiry',         label: 'Expiry' },
  { id: 'addressed',      label: 'Addressed' },
  { id: 'skipped',        label: 'Skipped' },
];

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

// ── Single action card ────────────────────────────────────────────────────────
function ActionCard({ action, onAddress, onDismiss, onUndo, addressing, dismissing, undoing, notes, onNotesChange, previewLocked = false, previewMode = false, onAddToOrder, isInCart }) {
  const [expanded, setExpanded] = useState(false);

  const typeMeta   = TYPE_META[action.type]    || TYPE_META.reorder;
  const colorClass = COLOR_CLASSES[typeMeta.color] || COLOR_CLASSES.blue;
  const Icon       = typeMeta.icon;
  const isAddressed = action.status === 'addressed';
  const isSkipped   = action.status === 'dismissed';
  const isInactive  = isAddressed || isSkipped;
  const displayValue = isInactive
    ? (action.anchor_value != null ? action.anchor_value : action.identified_value)
    : action.identified_value;

  const showOrderBtn = action.type === 'reorder' && !isInactive && !previewLocked && !previewMode && onAddToOrder;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      isInactive ? 'border-border bg-muted/30 opacity-70' : 'border-border bg-card hover:border-primary/30'
    }`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Click-to-expand region */}
        <div
          className={`flex items-center gap-3 flex-1 min-w-0 ${!previewLocked && !isInactive ? 'cursor-pointer' : 'cursor-default'}`}
          onClick={() => {
            if (previewLocked) return;
            if (!isInactive) setExpanded(e => !e);
          }}
        >
          <Icon className={`h-4 w-4 shrink-0 ${colorClass.icon}`} />
          <span className={`flex-1 text-sm font-medium truncate ${isInactive ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
            {action.headline}
          </span>
        </div>

        {/* Value badge */}
        {displayValue > 0 && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${colorClass.badge}`}>
            {previewLocked
              ? <span style={{ filter: 'blur(5px)' }} className="select-none">{fmt(displayValue)}</span>
              : fmt(displayValue)
            }
          </span>
        )}

        {/* Add to Order button — only for reorder type */}
        {showOrderBtn && (
          <button
            onClick={e => { e.stopPropagation(); onAddToOrder(action); }}
            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-colors shrink-0 whitespace-nowrap ${
              isInCart
                ? 'bg-blue-50 text-blue-700 border-blue-200 cursor-default'
                : 'text-blue-600 border-blue-200 hover:bg-blue-50'
            }`}
          >
            <ShoppingCart className="h-3 w-3" />
            {isInCart ? 'Added ✓' : 'Add to Order'}
          </button>
        )}

        {/* Action buttons — only for open, non-locked tiles */}
        {!isInactive && !previewLocked && !previewMode && (
          <div className="flex items-center gap-1 shrink-0 ml-2">
            <button
              onClick={e => { e.stopPropagation(); onAddress(action.id, notes); }}
              disabled={addressing || dismissing}
              className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              <CheckCircle2 className="h-3 w-3" />
              {addressing ? 'Saving…' : 'Done'}
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDismiss(action.id); }}
              disabled={addressing || dismissing}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-3 w-3" />
              {dismissing ? '…' : 'Skip'}
            </button>
          </div>
        )}

        {/* Status/chevron icon */}
        <div
          className={!previewLocked && !isInactive ? 'cursor-pointer' : 'cursor-default'}
          onClick={() => {
            if (previewLocked) return;
            if (!isInactive) setExpanded(e => !e);
          }}
        >
          {previewLocked ? (
            <Lock className="h-4 w-4 text-muted-foreground/40 shrink-0" />
          ) : isAddressed ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
          ) : isSkipped ? (
            <SkipForward className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            expanded
              ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
        </div>
      </div>

      {isAddressed && !previewLocked && (
        <div className="px-4 pb-3 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {action.addressed_at ? `Addressed ${action.addressed_at.slice(0, 10)}` : 'Addressed'}
            {action.notes && <span className="ml-2 italic">"{action.notes}"</span>}
          </span>
          <button
            onClick={() => onUndo(action.id)}
            disabled={undoing}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-2.5 py-1 transition-colors disabled:opacity-50 shrink-0"
          >
            <Undo2 className="h-3 w-3" />
            {undoing ? 'Undoing…' : 'Undo'}
          </button>
        </div>
      )}

      {isSkipped && !previewLocked && (
        <div className="px-4 pb-3 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {action.dismissed_at ? `Skipped ${action.dismissed_at.slice(0, 10)}` : 'Skipped'}
          </span>
          <button
            onClick={() => onUndo(action.id)}
            disabled={undoing}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-2.5 py-1 transition-colors disabled:opacity-50 shrink-0"
          >
            <Undo2 className="h-3 w-3" />
            {undoing ? 'Undoing…' : 'Undo'}
          </button>
        </div>
      )}

      {expanded && !isInactive && !previewLocked && (
        <div className="px-4 pb-4 border-t border-border space-y-3 pt-3">
          <p className="text-sm text-foreground/80 leading-relaxed">{action.suggestion}</p>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Notes (optional — saved when you click Done)</label>
            <input
              type="text"
              value={notes}
              onChange={e => onNotesChange(e.target.value)}
              placeholder="e.g. Called supplier, placed order…"
              className="w-full border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </div>
          {previewMode && (
            <p className="text-xs text-muted-foreground italic flex items-center gap-1">
              <Lock className="w-3 h-3 shrink-0" />
              Upgrade to Growth to act on this
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Priority group ────────────────────────────────────────────────────────────
function PriorityGroup({ priority, actions, onAddress, onDismiss, addressingIds, dismissingIds, notesMap, onNotesChange, previewMode = false, flatPreviewIds = [], onAddToOrder, cartIds }) {
  const [open, setOpen] = useState(true);
  const meta = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <div>
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 mb-2 w-full">
        <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
        <span className={`text-xs font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${meta.bg} ${meta.text}`}>{actions.length}</span>
        <span className="ml-auto text-muted-foreground">
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </span>
      </button>
      {open && (
        <div className="space-y-2 mb-5">
          {actions.map(a => (
            <ActionCard
              key={a.id}
              action={a}
              onAddress={onAddress}
              onDismiss={onDismiss}
              addressing={addressingIds.has(a.id)}
              dismissing={dismissingIds.has(a.id)}
              notes={notesMap[a.id] || ''}
              onNotesChange={(v) => onNotesChange(a.id, v)}
              previewMode={previewMode}
              previewLocked={previewMode && !flatPreviewIds.includes(a.id)}
              onAddToOrder={onAddToOrder}
              isInCart={cartIds.has(a.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const SmartActions = ({ previewMode = false }) => {
  const qc                               = useQueryClient();
  const { storeProfile, user }           = useAuth();
  const storeName                        = storeProfile?.shop_name || user?.shop_name || 'Our Store';

  const [filter, setFilter]              = useState('all');
  const [addressingIds, setAddressingIds] = useState(new Set());
  const [dismissingIds, setDismissingIds] = useState(new Set());
  const [undoingIds,    setUndoingIds]    = useState(new Set());
  const [undoingAll,      setUndoingAll]      = useState(false);
  const [isRegenerating,  setIsRegenerating]  = useState(false);
  const [notesMap,        setNotesMap]        = useState({});
  const [searchQuery,   setSearchQuery]   = useState('');
  const [saCategory,    setSaCategory]    = useState('');

  // ── Order cart state ──
  const [orderCart,        setOrderCart]        = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [showWhatsApp,     setShowWhatsApp]     = useState(false);
  const [lastSentAt,       setLastSentAt]        = useState(
    () => localStorage.getItem('kf2_order_last_sent_at')
  );

  // ── React Query: open + addressed + skipped lists, each cached 5 min ──
  const results = useQueries({
    queries: [
      { queryKey: ['smart-actions-open'],      queryFn: () => smartActionsAPI.getAll({ status: 'open' }).then(r => r.data ?? []) },
      { queryKey: ['smart-actions-addressed'], queryFn: () => smartActionsAPI.getAll({ status: 'addressed' }).then(r => r.data ?? []) },
      { queryKey: ['smart-actions-skipped'],   queryFn: () => smartActionsAPI.getAll({ status: 'dismissed' }).then(r => r.data ?? []) },
    ],
  });

  // ── Stock products for category filtering and pack_size lookup ──
  const { data: stockProducts = [] } = useQuery({
    queryKey: ['stock-products'],
    queryFn: () => stockAPI.getAll({ limit: 500 }).then(r => r.data?.items ?? r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  // ── Suppliers for order cart dropdown ──
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => suppliersAPI.getAll().then(r => r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const categoryMap = useMemo(() => {
    const m = {};
    stockProducts.forEach(p => { if (p.catalog_id && p.category) m[p.catalog_id] = p.category; });
    return m;
  }, [stockProducts]);

  const packSizeMap = useMemo(() => {
    const m = {};
    stockProducts.forEach(p => { if (p.catalog_id) m[p.catalog_id] = p.pack_size || ''; });
    return m;
  }, [stockProducts]);

  const saCategories = useMemo(() => {
    const set = new Set(stockProducts.map(p => p.category).filter(Boolean));
    return [...set].sort();
  }, [stockProducts]);

  const loading          = results.some(r => r.isLoading);
  const openActions      = results[0].data ?? [];
  const addressedActions = results[1].data ?? [];
  const skippedActions   = results[2].data ?? [];

  // ── Order cart handlers ──
  const cartIds = useMemo(() => new Set(orderCart.map(i => i.action_id)), [orderCart]);

  const handleAddToOrder = (action) => {
    if (cartIds.has(action.id)) return;
    const reorderQty = action.snapshot?.reorder_qty || 1;
    setOrderCart(prev => [...prev, {
      action_id:    action.id,
      catalog_id:   action.catalog_id,
      product_name: action.product_name || action.headline?.split('—')[0]?.trim() || 'Unknown',
      pack_size:    packSizeMap[action.catalog_id] || '',
      quantity:     reorderQty,
    }]);
  };

  const handleCartUpdateQty = (action_id, qty) => {
    setOrderCart(prev => prev.map(it => it.action_id === action_id ? { ...it, quantity: qty } : it));
  };

  const handleCartRemove = (action_id) => {
    setOrderCart(prev => prev.filter(it => it.action_id !== action_id));
  };

  const handleAddress = async (id, notes) => {
    setAddressingIds(s => new Set([...s, id]));
    try {
      await smartActionsAPI.address(id, notes);
      const action = openActions.find(a => a.id === id);
      qc.setQueryData(['smart-actions-open'],      prev => (prev || []).filter(a => a.id !== id));
      qc.setQueryData(['stock-actions'],           prev => (prev || []).filter(a => a.id !== id));
      if (action) {
        const addressed = { ...action, status: 'addressed', addressed_at: new Date().toISOString().slice(0, 10), notes };
        qc.setQueryData(['smart-actions-addressed'], prev => [addressed, ...(prev || [])]);
        qc.setQueryData(['stock-actions-addressed'], prev => [addressed, ...(prev || [])]);
      }
      setNotesMap(m => { const n = { ...m }; delete n[id]; return n; });
      toast.success('Action marked as addressed');
    } catch { toast.error('Could not update action'); }
    finally { setAddressingIds(s => { const n = new Set(s); n.delete(id); return n; }); }
  };

  const handleDismiss = async (id) => {
    setDismissingIds(s => new Set([...s, id]));
    try {
      await smartActionsAPI.dismiss(id);
      const action = openActions.find(a => a.id === id);
      qc.setQueryData(['smart-actions-open'], prev => (prev || []).filter(a => a.id !== id));
      qc.setQueryData(['stock-actions'],      prev => (prev || []).filter(a => a.id !== id));
      if (action) {
        const skipped = { ...action, status: 'dismissed', dismissed_at: new Date().toISOString().slice(0, 10) };
        qc.setQueryData(['smart-actions-skipped'],   prev => [skipped, ...(prev || [])]);
        qc.setQueryData(['stock-actions-dismissed'], prev => [skipped, ...(prev || [])]);
      }
    } catch { toast.error('Could not skip action'); }
    finally { setDismissingIds(s => { const n = new Set(s); n.delete(id); return n; }); }
  };

  const handleUndo = async (id) => {
    setUndoingIds(s => new Set([...s, id]));
    try {
      await smartActionsAPI.reopen(id);
      qc.setQueryData(['smart-actions-addressed'], prev => (prev || []).filter(a => a.id !== id));
      qc.setQueryData(['smart-actions-skipped'],   prev => (prev || []).filter(a => a.id !== id));
      qc.setQueryData(['stock-actions-addressed'], prev => (prev || []).filter(a => a.id !== id));
      qc.setQueryData(['stock-actions-dismissed'], prev => (prev || []).filter(a => a.id !== id));
      qc.invalidateQueries({ queryKey: ['smart-actions-open'] });
      qc.invalidateQueries({ queryKey: ['stock-actions'] });
      toast.success('Action undone. Smart actions refreshed.');
    } catch { toast.error('Could not undo action'); }
    finally { setUndoingIds(s => { const n = new Set(s); n.delete(id); return n; }); }
  };

  const handleUndoAll = async (actions) => {
    setUndoingAll(true);
    try {
      for (const a of actions) { await smartActionsAPI.reopen(a.id); }
      qc.setQueryData(['smart-actions-addressed'], []);
      qc.setQueryData(['smart-actions-skipped'],   []);
      qc.setQueryData(['stock-actions-addressed'], []);
      qc.setQueryData(['stock-actions-dismissed'], []);
      qc.invalidateQueries({ queryKey: ['smart-actions-open'] });
      qc.invalidateQueries({ queryKey: ['stock-actions'] });
      toast.success(`${actions.length} action${actions.length !== 1 ? 's' : ''} undone. Smart actions refreshed.`);
    } catch { toast.error('Could not undo all actions'); }
    finally { setUndoingAll(false); }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      await smartActionsAPI.regenerate();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['smart-actions-open'] }),
        qc.invalidateQueries({ queryKey: ['smart-actions-addressed'] }),
        qc.invalidateQueries({ queryKey: ['smart-actions-skipped'] }),
      ]);
      toast.success('Smart actions refreshed');
    } catch { toast.error('Could not refresh smart actions'); }
    finally { setIsRegenerating(false); }
  };

  // ── Filtering ──
  const showAddressed = filter === 'addressed';
  const showSkipped   = filter === 'skipped';
  const showLowStock  = filter === 'low_stock';

  const displayActions = (showAddressed || showSkipped)
    ? []
    : openActions.filter(a => {
        if (filter === 'critical'  && a.priority !== 'critical') return false;
        if (filter === 'low_stock' && a.type !== 'reorder')      return false;
        if (filter !== 'all' && filter !== 'critical' && filter !== 'low_stock' && a.type !== filter) return false;
        if (searchQuery && !a.headline.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        if (saCategory && categoryMap[a.catalog_id] !== saCategory) return false;
        return true;
      });

  const counts = {
    all:            openActions.length,
    critical:       openActions.filter(a => a.priority === 'critical').length,
    dead_stock:     openActions.filter(a => a.type === 'dead_stock').length,
    low_stock:      openActions.filter(a => a.type === 'reorder').length,
    margin_leakage: openActions.filter(a => a.type === 'margin_leakage').length,
    expiry:         openActions.filter(a => a.type === 'expiry').length,
    addressed:      addressedActions.length,
    skipped:        skippedActions.length,
  };

  const byPriority = { critical: [], high: [], medium: [] };
  if (!showAddressed && !showSkipped) {
    for (const a of displayActions) {
      (byPriority[a.priority] || byPriority.medium).push(a);
    }
  }

  const flatPreviewIds = previewMode
    ? [...byPriority.critical, ...byPriority.high, ...byPriority.medium]
        .slice(0, 3)
        .map(a => a.id)
    : [];

  const totalIdentifiedValue = openActions.reduce((s, a) => s + (a.identified_value || 0), 0);
  const realizedValue        = addressedActions.reduce((s, a) => s + (a.anchor_value != null ? a.anchor_value : (a.identified_value || 0)), 0);

  // ── Render ──
  const renderActionList = () => {
    if (loading) {
      return (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />)}
        </div>
      );
    }
    if (showAddressed) {
      if (addressedActions.length === 0) return (
        <div className="text-center py-16 text-muted-foreground">
          <CheckCircle2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No addressed actions yet</p>
          <p className="text-sm mt-1">Actions you address will appear here.</p>
        </div>
      );
      return (
        <div className="space-y-2">
          <div className="flex justify-end mb-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button disabled={undoingAll} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50">
                  <Undo2 className="h-3.5 w-3.5" />
                  {undoingAll ? 'Undoing…' : `Undo All (${addressedActions.length})`}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Undo all addressed actions?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will move all {addressedActions.length} addressed action{addressedActions.length !== 1 ? 's' : ''} back to open and remove any realized savings credited to them. Smart actions will be refreshed. This cannot be undone in bulk.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleUndoAll(addressedActions)}>Undo All</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          {addressedActions.map(a => (
            <ActionCard key={a.id} action={a} onAddress={() => {}} onDismiss={() => {}} onUndo={handleUndo} addressing={false} dismissing={false} undoing={undoingIds.has(a.id)} notes="" onNotesChange={() => {}} />
          ))}
        </div>
      );
    }
    if (showSkipped) {
      if (skippedActions.length === 0) return (
        <div className="text-center py-16 text-muted-foreground">
          <SkipForward className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No skipped actions</p>
          <p className="text-sm mt-1">Actions you skip will appear here. Use Undo to bring one back.</p>
        </div>
      );
      return (
        <div className="space-y-2">
          <div className="flex justify-end mb-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button disabled={undoingAll} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50">
                  <Undo2 className="h-3.5 w-3.5" />
                  {undoingAll ? 'Undoing…' : `Undo All (${skippedActions.length})`}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Undo all skipped actions?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will move all {skippedActions.length} skipped action{skippedActions.length !== 1 ? 's' : ''} back to open. Smart actions will be refreshed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleUndoAll(skippedActions)}>Undo All</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          {skippedActions.map(a => (
            <ActionCard key={a.id} action={a} onAddress={() => {}} onDismiss={() => {}} onUndo={handleUndo} addressing={false} dismissing={false} undoing={undoingIds.has(a.id)} notes="" onNotesChange={() => {}} />
          ))}
        </div>
      );
    }
    if (displayActions.length === 0) return (
      <div className="text-center py-16 text-muted-foreground">
        <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="font-medium text-foreground">
          {filter === 'all' ? 'All caught up!' : `No ${FILTERS.find(f => f.id === filter)?.label.toLowerCase()} actions`}
        </p>
        <p className="text-sm mt-1">
          {filter === 'all'
            ? 'No open actions right now. Check back as your store data updates.'
            : 'Switch to "All" to see other open actions.'}
        </p>
      </div>
    );
    return (
      <div>
        {['critical', 'high', 'medium'].map(p =>
          byPriority[p].length > 0 ? (
            <PriorityGroup
              key={p}
              priority={p}
              actions={byPriority[p]}
              onAddress={handleAddress}
              onDismiss={handleDismiss}
              addressingIds={addressingIds}
              dismissingIds={dismissingIds}
              notesMap={notesMap}
              onNotesChange={(id, v) => setNotesMap(m => ({ ...m, [id]: v }))}
              previewMode={previewMode}
              flatPreviewIds={flatPreviewIds}
              onAddToOrder={showLowStock ? handleAddToOrder : null}
              cartIds={cartIds}
            />
          ) : null
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border bg-card">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Smart Actions</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                {openActions.length} open action{openActions.length !== 1 ? 's' : ''}
                {totalIdentifiedValue > 0 && <span> · {fmt(totalIdentifiedValue)} identified</span>}
                {realizedValue > 0 && <span className="text-emerald-600"> · {fmt(realizedValue)} realized</span>}
                {skippedActions.length > 0 && <span> · {skippedActions.length} skipped</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRegenerate}
              disabled={isRegenerating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border-2 border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-400 hover:text-white transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
              {isRegenerating ? 'Refreshing…' : 'Refresh'}
            </button>
            {saCategories.length > 0 && (
              <Select value={saCategory || '__all__'} onValueChange={v => setSaCategory(v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Categories</SelectItem>
                  {saCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search actions…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-1 overflow-x-auto mt-4">
          {FILTERS.map(f => {
            const count = counts[f.id];
            const isActive = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {f.label}
                {count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Preview mode upgrade banner */}
        {previewMode && !showAddressed && !showSkipped && (
          <div className="flex items-center gap-3 mt-3 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
            <Lock className="w-4 h-4 text-emerald-700 shrink-0" />
            <p className="text-sm text-emerald-800 flex-1">
              Viewing <strong>3 of {openActions.length}</strong> actions ·{' '}
              <strong>{Math.max(0, openActions.length - 3)}</strong> more locked
            </p>
            <Link
              to="/settings"
              className="text-xs font-bold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors"
            >
              Upgrade to Growth →
            </Link>
          </div>
        )}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {showLowStock && !showAddressed && !showSkipped ? (
          /* Two-panel layout for Low Stock filter */
          <div className="flex gap-4 h-full p-6">
            <div className="flex-1 min-w-0 overflow-auto">
              {renderActionList()}
            </div>
            <div className="w-72 shrink-0">
              <OrderCartPanel
                cart={orderCart}
                onUpdateQty={handleCartUpdateQty}
                onRemove={handleCartRemove}
                onClearAll={() => setOrderCart([])}
                suppliers={suppliers}
                selectedSupplier={selectedSupplier}
                onSupplierChange={setSelectedSupplier}
                onSend={() => setShowWhatsApp(true)}
                lastSentAt={lastSentAt}
              />
            </div>
          </div>
        ) : (
          /* Full-width layout for all other filters */
          <div className="h-full overflow-auto p-6">
            {renderActionList()}
          </div>
        )}
      </div>

      {/* WhatsApp Preview Modal */}
      {showWhatsApp && (
        <WhatsAppPreviewModal
          items={orderCart}
          supplierName={selectedSupplier}
          storeName={storeName}
          onClose={() => setShowWhatsApp(false)}
          onMarkSent={setLastSentAt}
        />
      )}
    </div>
  );
};

export default SmartActions;
